import { Injectable, inject } from '@angular/core';
import { 
  Auth, 
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut, 
  user, 
  sendPasswordResetEmail,
  multiFactor
} from '@angular/fire/auth';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, ReplaySubject, Observable, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private http = inject(HttpClient);
  private router = inject(Router);

  private _userSubject = new ReplaySubject<any>(1);
  user$: Observable<any> = this._userSubject.asObservable();
  
  private sessionToken = new BehaviorSubject<string | null>(null);
  systemRole$ = new BehaviorSubject<string>('SYSTEM_USER');
  // null = desconocido (auth aún cargando), false = no verificado, true = verificado
  emailVerified$ = new BehaviorSubject<boolean | null>(null);
  // true mientras no se haya resuelto el estado inicial de auth
  authLoading$ = new BehaviorSubject<boolean>(true);

  private authStateResolved = false;
  private resolveAuth!: () => void;
  public authReady = new Promise<void>((resolve) => this.resolveAuth = resolve);

  constructor() {
    user(this.auth).subscribe(u => {
      const isTestBot = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('test_token') : null;
      if (!isTestBot) {
        this._userSubject.next(u);
      }
    });

    this.user$.subscribe(async (u) => {
      if (!u) {
        this.sessionToken.next(null);
        this.systemRole$.next('SYSTEM_USER');
        this.emailVerified$.next(false);
        this.authLoading$.next(false);
          const isBoneyard = typeof window !== 'undefined' && window.navigator?.userAgent?.includes('HeadlessChrome');
          if (!this.authStateResolved) {
            this.authStateResolved = true;
            this.resolveAuth();
            if (!isBoneyard && this.router.url !== '/login' && this.router.url !== '/') {
              this.router.navigate(['/login']);
            }
          } else {
            if (!isBoneyard) {
              this.router.navigate(['/login']);
            }
          }
      } else {
        if (!this.sessionToken.value) {
          try {
            const idToken = await u.getIdToken();
            const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/admin', { idToken }));
            this.sessionToken.next(res.access_token);
            this.systemRole$.next(res.user?.systemRole || 'SYSTEM_USER');
            this.emailVerified$.next(res.user?.emailVerified || false);
            this.authLoading$.next(false);
            if (this.router.url === '/login' || this.router.url === '/') {
               this.router.navigate(['/dashboard']);
            }
          } catch(e: any) {
            console.error("Fallo al renovar token silenciosamente", e);
            await signOut(this.auth);
            this.sessionToken.next(null);
            this.systemRole$.next('SYSTEM_USER');
            this.emailVerified$.next(false);
          }
        }
        if (!this.authStateResolved) {
          this.authStateResolved = true;
          this.resolveAuth();
        }
      }
    });
  }

  lastKnownEmail: string | null = null;

  async login(email: string, pass: string) {
    try {
      this.lastKnownEmail = email;
      const testToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('test_token') : null;
      
      if (testToken) {
        try {
          const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/test-login', { email, token: testToken }));
          this.sessionToken.next(res.access_token);
          this.systemRole$.next(res.user?.systemRole || 'SUPER_ADMIN');
          this.emailVerified$.next(res.user?.emailVerified || true);
          
          const fakeUser = {
            uid: res.realUid || 'test-bot-uid',
            email: email,
            emailVerified: true,
            getIdToken: async () => 'mock-id-token',
            reload: async () => {}
          };
          this._userSubject.next(fakeUser);
          
          this.authLoading$.next(false);
          if (this.router.url === '/login' || this.router.url === '/') {
             this.router.navigate(['/dashboard']);
          }
          if (!this.authStateResolved) {
            this.authStateResolved = true;
            this.resolveAuth();
          }
          return undefined;
        } catch (e) {
          console.warn("Test login API bypass failed, falling back to real password auth", e);
        }
      }

      const cred = await signInWithEmailAndPassword(this.auth, email, pass);
      await this.completeLogin(cred);
      return undefined;
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const { getMultiFactorResolver } = await import('@angular/fire/auth');
        const mfaResolver = getMultiFactorResolver(this.auth, error);
        return { mfaRequired: true, resolver: mfaResolver };
      }
      throw error;
    }
  }

  async resolveMfaSignIn(resolver: any, code: string) {
    const { TotpMultiFactorGenerator } = await import('@angular/fire/auth');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      resolver.hints[0].uid,
      code
    );
    const cred = await resolver.resolveSignIn(assertion);
    await this.completeLogin(cred);
  }

  private async completeLogin(cred: any) {
    const idToken = await cred.user.getIdToken();
    try {
      const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/admin', { idToken }));
      this.sessionToken.next(res.access_token);
      this.systemRole$.next(res.user?.systemRole || 'SYSTEM_USER');
      this.emailVerified$.next(res.user?.emailVerified || false);
    } catch (error) {
      console.error("No autorizado por BFF", error);
      await this.auth.signOut();
      throw new Error('No autorizado por el servidor backend');
    }
  }

  // Captcha & MFA Setup Helpers
  
  async createInvisibleRecaptcha(containerId: string): Promise<any> {
    const { RecaptchaVerifier } = await import('@angular/fire/auth');
    return new RecaptchaVerifier(this.auth, containerId, {
      size: 'invisible'
    });
  }

  async checkBotBypass(token: string): Promise<boolean> {
    try {
      const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/check-bot', { token }));
      return res?.bypass === true;
    } catch (e) {
      return false;
    }
  }

  getAuthInstance() {
    return this.auth;
  }

  async generateTotpSecret() {
    const u = this.auth.currentUser;
    if (!u) throw new Error("No hay usuario activo");
    const { multiFactor, TotpMultiFactorGenerator } = await import('@angular/fire/auth');
    const session = await multiFactor(u).getSession();
    return await TotpMultiFactorGenerator.generateSecret(session);
  }

  async enrollTotp(totpSecret: any, code: string) {
    const u = this.auth.currentUser;
    if (!u) throw new Error("No hay usuario activo");
    const { TotpMultiFactorGenerator, multiFactor } = await import('@angular/fire/auth');
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, code);
    await multiFactor(u).enroll(assertion, 'Authenticator App');
  }

  isMfaEnrolled(): boolean {
    const u = this.auth.currentUser;
    if (!u) return false;
    return multiFactor(u).enrolledFactors.length > 0;
  }

  async sendVerificationEmail() {
    const u = this.auth.currentUser;
    if (u && u.email) {
      console.log('Sending verification email for', u.email);
      await firstValueFrom(this.http.post<any>('/api/bff/auth/verify-email', { email: u.email }));
    } else {
      console.warn('Cannot send verification email: missing user or email', u);
      // Fallback
      let fallbackEmail = '';
      if (u) fallbackEmail = u.email || '';
      if (!fallbackEmail) {
        // Force getting the user from the observable if currentUser is out of sync
        const asyncUser = await firstValueFrom(this.user$);
        if (asyncUser && asyncUser.email) {
          fallbackEmail = asyncUser.email;
        }
      }
      if (!fallbackEmail && this.lastKnownEmail) {
        fallbackEmail = this.lastKnownEmail;
      }
      
      if (fallbackEmail) {
        console.log('Sending verification email using fallback email:', fallbackEmail);
        await firstValueFrom(this.http.post<any>('/api/bff/auth/verify-email', { email: fallbackEmail }));
      } else {
        throw new Error('No se pudo determinar el correo del usuario activo. Por favor, vuelve a iniciar sesión.');
      }
    }
  }

  async resetPassword(email: string) {
    const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/recover', { email }));
    if (!res.success) {
      throw new Error(res.error || 'Failed to send recovery email');
    }
  }

  async logout() {
    await signOut(this.auth);
    this.sessionToken.next(null);
    this.systemRole$.next('SYSTEM_USER');
    this.emailVerified$.next(false);
    this.router.navigate(['/login']);
  }

  async reloadAuthToken(): Promise<boolean> {
    const u = this.auth.currentUser;
    if (!u) return false;
    
    await u.reload();
    try {
      const idToken = await u.getIdToken(true); // Fuerza refresh del JWT
      const res = await firstValueFrom(this.http.post<any>('/api/bff/auth/admin', { idToken }));
      this.sessionToken.next(res.access_token);
      this.systemRole$.next(res.user?.systemRole || 'SYSTEM_USER');
      this.emailVerified$.next(res.user?.emailVerified || false);
      return res.user?.emailVerified || false;
    } catch(e) {
      console.error("Failed to reload auth token", e);
      return false;
    }
  }

  getToken() {
    return this.sessionToken.value;
  }

  async waitForToken(): Promise<string | null> {
    await this.authReady;
    return this.sessionToken.value;
  }
}
